import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { airtableService } from '../services/airtable';
import { formatToLocalDateTime } from '../utils/timeUtils';

const SessionHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [evaluationCounts, setEvaluationCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const records = await airtableService.getSessionHistory(50);
      setSessions(records);

      const counts = {};
      await Promise.all(records.map(async (session) => {
        const sessionId = session.fields['Session ID'];
        if (!sessionId) {
          return;
        }
        const evals = await airtableService.getSessionEvaluations(sessionId);
        counts[sessionId] = evals.length;
      }));
      setEvaluationCounts(counts);
    } catch (err) {
      console.error('Error loading session history:', err);
      setError('Failed to load past sessions.');
    } finally {
      setLoading(false);
    }
  };

  const openSession = (sessionId) => {
    if (!sessionId) return;
    navigate(`/dr-grant/summary/${encodeURIComponent(sessionId)}`);
  };

  return (
    <div className="container">
      <div className="header">
        <h1>Past Sessions</h1>
        <p>Review evaluations from earlier clinic sessions.</p>
      </div>

      {error && (
        <div style={{
          color: 'var(--coral)',
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#ffe6e6',
          borderRadius: '6px'
        }}>
          {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <p style={{ color: 'var(--text-light)', textAlign: 'center', padding: '24px' }}>
            Loading past sessions...
          </p>
        ) : sessions.length === 0 ? (
          <p style={{ color: 'var(--text-light)', textAlign: 'center', padding: '24px' }}>
            No past sessions found.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {sessions.map((session) => {
              const sessionId = session.fields['Session ID'];
              const status = session.fields['Status'] || 'unknown';
              const evalCount = sessionId ? (evaluationCounts[sessionId] || 0) : 0;

              return (
                <button
                  key={session.id}
                  onClick={() => openSession(sessionId)}
                  disabled={!sessionId}
                  style={{
                    textAlign: 'left',
                    padding: '16px',
                    border: '2px solid var(--light-blue)',
                    borderRadius: '8px',
                    backgroundColor: status === 'active' ? 'var(--warm-cream)' : 'white',
                    cursor: sessionId ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 'bold', color: 'var(--text-dark)', marginBottom: '4px' }}>
                      {session.fields['Clinic Code'] || 'Unknown Clinic'}
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        borderRadius: '10px',
                        backgroundColor: status === 'active' ? 'var(--muted-sage)' : 'var(--text-light)',
                        color: 'white'
                      }}>
                        {status}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                      Started {formatToLocalDateTime(session.fields['Start Time'])}
                      {session.fields['End Time'] && (
                        <> · Ended {formatToLocalDateTime(session.fields['End Time'])}</>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-light)', marginTop: '2px' }}>
                      Doctor: {session.fields['Doctor Name'] || '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: 'var(--primary-blue)'
                    }}>
                      {evalCount}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      evaluation{evalCount === 1 ? '' : 's'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', marginTop: '24px' }}>
        <Link to="/dr-grant/dashboard" className="btn btn-primary">
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};

export default SessionHistory;
